import React, { useMemo } from 'react';
import { gql } from '@soundxyz/gql-string';
import { ERROR_TYPE } from '@soundxyz/utils/constants/logging';
import { TopCollectorsRankedListItem } from '@components/Landing/sections/TopCollectors/TopCollectorsRankedListItem';
import { LandingRankedList } from '@components/Landing/shared/LandingRankedList';
import { TopCollectorsLandingDocument, useQuery } from '@graphql/reactQuery';
import { logError } from '@hooks/useLogErrorNew';
import {
  CollectorsTopChartsPathname,
  defaultCollectorsTopChartsPageSort,
} from '@pages-shared/topCharts';
import { CLIENT_QUERY_REDUCTION_ON_FRESH_SSR_STRATEGY } from '@utils/constants';
import { webappUriPath } from '@utils/url';

const TOP_COLLECTORS_CHART_LINK = CollectorsTopChartsPathname({
  timespan: '7d',
  sort: defaultCollectorsTopChartsPageSort,
});

/*#__PURE__*/ gql(/* GraphQL */ `
  query TopCollectorsLanding {
    topCollectorsV2(timePeriod: SEVEN_DAY, sort: NFTS_BOUGHT, limit: 12) {
      ...TopCollectorsRankedListItem
    }
  }
`);

export const TopCollectors = () => {
  const { data, isInitialLoading } = useQuery(TopCollectorsLandingDocument, {
    ...CLIENT_QUERY_REDUCTION_ON_FRESH_SSR_STRATEGY,
  });

  const topCollectorItems = useMemo(
    () =>
      data?.data.topCollectorsV2.map((topCollector, index) => (
        <TopCollectorsRankedListItem
          key={`top-collector-ranked-list-item-${index}`}
          topCollectorFrag={topCollector}
          rank={index + 1}
        />
      )) ?? [],
    [data],
  );

  if (!topCollectorItems?.length && !isInitialLoading) {
    logError({
      error: new Error('No Top Collectors'),
      message: 'Top Collectors on landing page is empty',
      feature: 'LANDING_PAGE',
      errorType: ERROR_TYPE.TOP_COLLECTORS_FETCH_ERROR,
      level: 'fatal',
      action: ERROR_TYPE.TOP_COLLECTORS_FETCH_ERROR,
      isArtistOnlyPage: false,
    });
    return null;
  }

  return (
    <LandingRankedList
      headingProps={{
        header: 'Top Collectors This Week',
        pathnameUri: webappUriPath(TOP_COLLECTORS_CHART_LINK),
      }}
      imageRadius="rounded-full"
      isLoading={isInitialLoading}
      numCols={3}
      numRows={4}
      items={topCollectorItems}
      id="top-collectors"
    />
  );
};
